<template>
  <div class="property-tab">
    <div class="appearance-grid">
      <span class="appearance-label">Body:</span>
      <div class="appearance-controls">
        <v-menu location="right" :close-on-content-click="false" @update:modelValue="colorPickerHandler">
          <template #activator="{ props: menu }">
            <v-tooltip location="bottom">
              <template #activator="{ props: tooltip }">
                <v-btn class="button-region-fill-color" size="small" :color="regionColor" v-bind="{ ...menu, ...tooltip }">
                  <v-icon>mdi-format-color-fill</v-icon>
                </v-btn>
              </template>
              <span>Change Region Color</span>
            </v-tooltip>
          </template>
          <v-color-picker v-model="regionColor" class="overflow-hidden" elevation="15" :class="{ disabled: PreferenceStore.INSTANCE.isReadOnly }"></v-color-picker>
        </v-menu>
      </div>
      <span class="appearance-label">Border:</span>
      <div class="appearance-controls">
        <v-menu location="right" :close-on-content-click="false" @update:modelValue="colorPickerHandler">
          <template #activator="{ props: menu }">
            <v-tooltip location="bottom">
              <template #activator="{ props: tooltip }">
                <v-btn class="button-region-border-color" size="small" :color="lineColor" v-bind="{ ...menu, ...tooltip }">
                  <v-icon>mdi-format-color-fill</v-icon>
                </v-btn>
              </template>
              <span>Change Border Color</span>
            </v-tooltip>
          </template>
          <v-color-picker v-model="lineColor" elevation="15" class="overflow-hidden" :class="{ disabled: PreferenceStore.INSTANCE.isReadOnly }"></v-color-picker>
        </v-menu>
        <v-slider
          v-model="borderWidth"
          class="ml-2"
          hide-details
          min="0" max="10" step="1"
          track-fill-color="primary" track-color="grey" thumb-color="primary"
          :disabled="PreferenceStore.INSTANCE.isReadOnly"
          v-on="{ mousedown: () => startBatchCommand(), mouseup: () => stopBatchCommand() }"
        ></v-slider>
        <span class="slider-value">{{ borderWidth }}</span>
      </div>
      <span class="appearance-label">Name:</span>
      <div class="appearance-controls">
        <v-menu location="right" :close-on-content-click="false" @update:modelValue="colorPickerHandler">
          <template #activator="{ props: menu }">
            <v-tooltip location="bottom">
              <template #activator="{ props: tooltip }">
                <v-btn class="button-region-name-color" size="small" :color="textColor" v-bind="{ ...menu, ...tooltip }">
                  <v-icon>mdi-format-color-fill</v-icon>
                </v-btn>
              </template>
              <span>Change Name Color</span>
            </v-tooltip>
          </template>
          <v-color-picker v-model="textColor" elevation="15" class="overflow-hidden" mode="rgba" :class="{ disabled: PreferenceStore.INSTANCE.isReadOnly }"></v-color-picker>
        </v-menu>
        <v-slider
          v-model="fontSize"
          class="ml-2"
          hide-details
          min="6" max="26" step="1"
          track-fill-color="primary" track-color="grey" thumb-color="primary"
          :disabled="PreferenceStore.INSTANCE.isReadOnly"
          v-on="{ mousedown: () => startBatchCommand(), mouseup: () => stopBatchCommand() }"
        ></v-slider>
        <span class="slider-value">{{ fontSize }}</span>
      </div>
    </div>
    <div class="appearance-footer">
      <v-btn size="small" color="primary" :disabled="PreferenceStore.INSTANCE.isReadOnly" @click="resetDefaults()">Reset Defaults</v-btn>
    </div>
  </div>
</template>

<script setup lang="ts">
import { Region } from "../mvc/region";
import RappidContext from "../rappid/rappid-context";
import { inject, computed, onBeforeUnmount, ref } from "vue";
import { PreferenceStore } from "../services/preference-store";

const colorPicking = ref<boolean>(false);
const rappidContext: RappidContext = inject<RappidContext>("rappidContext");

const compProps = defineProps<{
  model: Region;
}>();

onBeforeUnmount(() => {
  if (colorPicking.value) {
    rappidContext.commandManager.storeBatchCommand();
  }
});

function colorPickerHandler(value: boolean) {
  colorPicking.value = value;
  if (value) {
    startBatchCommand();
  } else {
    stopBatchCommand();
  }
}

function startBatchCommand() {
  rappidContext.commandManager.initBatchCommand();
}

function stopBatchCommand() {
  rappidContext.commandManager.storeBatchCommand();
}

function resetDefaults() {
  startBatchCommand();
  compProps.model.resetDefaults();
  stopBatchCommand();
}

const regionColor = computed({
  get(): string {
    return compProps.model.getFillColor();
  },
  set(color: string) {
    compProps.model.setFillColor(color);
  },
});

const lineColor = computed({
  get(): string {
    return compProps.model.getBorderColor();
  },
  set(color: string) {
    compProps.model.setBorderColor(color);
  },
});

const textColor = computed({
  get(): string {
    return compProps.model.getNameColor();
  },
  set(color: string) {
    compProps.model.setNameColor(color);
  },
});

const fontSize = computed({
  get(): number {
    return compProps.model.getNameFontSize();
  },
  set(size: number) {
    compProps.model.setNameFontSize(size);
  },
});

const borderWidth = computed({
  get(): number {
    return compProps.model.getBorderWidth();
  },
  set(width: number) {
    compProps.model.setBorderWidth(width);
  },
});
</script>
